import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {jwtDecode} from 'jwt-decode';
import {
    clearAccessToken,
    getAccessToken,
    isAccessTokenExpired,
    loginWithPassword,
    logoutSession,
    notifySessionExpired,
    refreshAccessToken,
    setAccessToken,
    setSessionExpiredHandler,
} from './authSession';

export const AuthContext = createContext(null);
const publicRoutes = ['/login', '/forgot-password', '/reset-password', '/login/mfa-challenge', '/login/mfa-setup'];

const normaliseClaimValue = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0 ? value[0] : null;
    }

    return value ?? null;
};

const hasAdminRole = (roleClaim) => {
    if (Array.isArray(roleClaim)) {
        return roleClaim.includes('Admin');
    }

    return roleClaim === 'Admin';
};

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [organisation, setOrganisation] = useState(null);
    const [email, setEmail] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const clearAuthState = useCallback(() => {
        clearAccessToken();
        setIsAuthenticated(false);
        setIsAdmin(false);
        setOrganisation(null);
        setEmail(null);
    }, []);

    const applyAccessToken = useCallback((token) => {
        setAccessToken(token);

        const decodedToken = jwtDecode(token);
        const emailClaim = normaliseClaimValue(decodedToken.email);
        const organisationClaim = normaliseClaimValue(decodedToken.Organisation);
        setIsAuthenticated(true);
        setIsAdmin(hasAdminRole(decodedToken.role));
        setOrganisation(organisationClaim);
        setEmail(emailClaim);
    }, []);

    useEffect(() => {
        let isActive = true;

        const initializeAuthentication = async () => {
            try {
                const token = getAccessToken();
                if (token && !isAccessTokenExpired(token)) {
                    applyAccessToken(token);
                    return;
                }

                const refreshedToken = await refreshAccessToken();
                if (isActive) {
                    applyAccessToken(refreshedToken);
                }
            } catch {
                if (isActive) {
                    clearAuthState();
                }
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        initializeAuthentication();

        return () => {
            isActive = false;
        };
    }, [applyAccessToken, clearAuthState]);

    useEffect(() => {
        setSessionExpiredHandler(() => {
            clearAuthState();
            if (!publicRoutes.includes(location.pathname)) {
                navigate('/login', {replace: true});
            }
        });

        return () => {
            setSessionExpiredHandler(null);
        };
    }, [clearAuthState, location.pathname, navigate]);

    useEffect(() => {
        if (!loading && !isAuthenticated && !publicRoutes.includes(location.pathname)) {
            navigate('/login', {replace: true});
        }
    }, [isAuthenticated, loading, location.pathname, navigate]);

    const handleLogin = async (email, password) => {
        const data = await loginWithPassword(email, password);
        if (data.requiresMfa || data.requiresMfaEnrolment) {
            return data;
        }
        applyAccessToken(data.token);
        return data;
    };

    const handleLogout = () => {
        // Optimistic logout: tear down client state and navigate immediately so the user
        // isn't blocked by the server-side refresh-token revocation round-trip. The access
        // token is cleared in-memory here, so nothing authenticated can be called from this
        // tab even if the server call below is delayed or fails.
        notifySessionExpired();
        navigate('/login', {replace: true});

        // Fire-and-forget; server logs revocation failures. Swallow here to avoid an
        // unhandled promise rejection. If the request never reaches the server, the
        // refresh cookie lives until its natural expiry on the client.
        logoutSession().catch(() => {});
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            isAdmin,
            organisation,
            email,
            handleLogin,
            handleLogout,
            applyAccessToken
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
